
import { TreeUtils } from './tree-utils.js';
import { fileSystem } from '../../backup-export/api/filesystem.js';
import { restoreTreeUiStateAfterLoad } from './tree-ui-persist.js';
import { consumePendingCurriculumSwitcher } from '../../editor/api/curriculum-switcher-pending.js';
import { scheduleSearchIndexRebuild } from '../../search/api/search-index-service.js';
import { maybeNotifyCareDue } from '../../garden-progress/api/care-reminders.js';
import { runThrottledBackgroundTask } from '../../../shared/lib/background-task-gate.js';
import { maybeNotifyRemoteVersionUpdate } from '../../version-updates/api/version-update-notify.js';
import { parseFolderReadme } from '../../../shared/lib/arborito-archive.js';

export const DataProcessor = {
    /**
     * Processes the raw JSON tree into the application state.
     * Handles language fallback, exam prefixes, empty states, and hydration.
     * @param {{ suppressReadmeAutoOpen?: boolean, carryOverSelection?: boolean }} [options], set true for in-place graph refreshes (save, graph CRUD) so the readme intro does not reopen. `carryOverSelection`: re-bind open lesson; default true; `mountCurriculum` passes false when switching source.
     */
    process(store, json, finalSource, options = {}) {
        const { suppressReadmeAutoOpen = false, carryOverSelection: carryOverride } = options;
        const carryOverSelection = carryOverride !== undefined ? !!carryOverride : true;
        const ui = store.ui; // Access UI strings via store proxy (loadData must have loaded i18n first).

        /** Root-only JSON: no `languages` wrapper with entries (single root node shape). */
        const langsBefore = json && typeof json === 'object' ? json.languages : null;
        const hadLanguageMap =
            langsBefore &&
            typeof langsBefore === 'object' &&
            !Array.isArray(langsBefore) &&
            Object.keys(langsBefore).length > 0;
        if (json && typeof json === 'object' && !hadLanguageMap) {
            const t = json.type;
            const looksLikeRoot =
                t === 'root' ||
                (t === 'branch' && json.id != null) ||
                (Array.isArray(json.children) && json.id != null);
            if (looksLikeRoot) {
                const code =
                    store.state.lang && String(store.state.lang).length >= 2
                        ? String(store.state.lang).toUpperCase().slice(0, 8)
                        : 'EN';
                json = { languages: { [code]: json } };
            }
        }

        const langsAfter = json && typeof json === 'object' ? json.languages : null;
        const hasLanguageMap =
            langsAfter &&
            typeof langsAfter === 'object' &&
            !Array.isArray(langsAfter) &&
            Object.keys(langsAfter).length > 0;

        let langData = null;
        if (json && hasLanguageMap) {
            const availableLangs = Object.keys(json.languages);
            /* Normalise to ASCII uppercase + drop region separators so `pt_BR`, `pt-BR`,
             * `PT_br` etc. all collide on the same bucket. This is intentional: the user's
             * UI lang is `ES`, `PT_BR`, etc. and a tree may declare any close variant. We
             * still keep the original keys for the actual lookup. */
            const norm = (c) => String(c || '').trim().toUpperCase().replace(/[-_]/g, '');
            /** Base of a code (`PT_BR` → `PT`) used for cross-region fallback. */
            const base = (c) => norm(c).slice(0, 2);
            const keyByNorm = new Map(availableLangs.map((k) => [norm(k), k]));
            const keyByBase = new Map();
            for (const k of availableLangs) {
                const b = base(k);
                if (b && !keyByBase.has(b)) keyByBase.set(b, k);
            }

            const uiLang = store.state.lang;
            const editLang = store.state.curriculumEditLang;

            /* Resolution order:
             *   1) Construction mode + an explicit edit-lang the user picked, never
             *      override it, the user is actively translating that variant.
             *   2) Exact match on the user's UI language (normalised). This is the
             *      main "show the tree in the user's language" path.
             *   3) Same base language, different region (`UI = pt_BR` ↔ `tree = pt`).
             *   4) First declared language as last-ditch fallback (no silent error). */
            let resolvedKey = null;
            if (
                store.state.constructionMode &&
                editLang &&
                keyByNorm.has(norm(editLang))
            ) {
                resolvedKey = keyByNorm.get(norm(editLang));
            } else if (uiLang && keyByNorm.has(norm(uiLang))) {
                resolvedKey = keyByNorm.get(norm(uiLang));
            } else if (uiLang && keyByBase.has(base(uiLang))) {
                resolvedKey = keyByBase.get(base(uiLang));
            } else if (availableLangs.length > 0) {
                resolvedKey = availableLangs[0];
            }
            langData = resolvedKey ? json.languages[resolvedKey] : null;
            if (!langData && availableLangs.length > 0) {
                langData = json.languages[availableLangs[0]];
            }
        }

        if (!langData) {
            store.update({ loading: false, error: ui.errorNoContent || "No valid content found in this tree." });
            return;
        }
        
        // Post-processing: Exam Prefixes & Empty State Detection
        const examPrefix = ui.examLabelPrefix || "Exam: ";
        
        const processNode = (node) => {
            if (!node || typeof node !== 'object') return;
            // 1. Exam Prefix (name may be missing or non-string in bad data)
            if (node.type === 'exam') {
                const rawName = node.name == null ? '' : String(node.name);
                node.name = rawName.startsWith(examPrefix) ? rawName : examPrefix + rawName;
            }
            
            // 2. Empty State Detection (Fix for immediate visual feedback)
            // If it is a container (branch/root), has no children, and is not waiting for network (unloaded)
            if ((node.type === 'branch' || node.type === 'root') && 
                (!node.children || node.children.length === 0) && 
                !node.hasUnloadedChildren) {
                node.isEmpty = true;
            }

            if ((node.type === 'branch' || node.type === 'root') && node.content) {
                try {
                    const folderMeta = parseFolderReadme(String(node.content));
                    if (folderMeta.icon) node.icon = folderMeta.icon;
                    if (folderMeta.description != null) node.description = folderMeta.description;
                    if ('certifiable' in folderMeta) node.isCertifiable = !!folderMeta.certifiable;
                } catch {
                    /* ignore malformed README */
                }
            }

            if (Array.isArray(node.children)) node.children.forEach(processNode);
        };
        
        processNode(langData);
        
        // SMART HYDRATION: Expand completed branches into leaves in memory
        this.hydrateCompletionState(store, langData);

        /**
         * Only re-bind lesson/preview when refreshing the SAME source (e.g. save to local).
         * After `mountCurriculum`, `activeSource` already points at the destination; comparing ids alone
         * would false-positive when switching local garden → network tree with different ids, the flag comes from mount.
         */
        const prevSel = store.state.selectedNode;
        const prevPreview = store.state.previewNode;
        let nextSelected = null;
        let nextPreview = null;
        if (carryOverSelection) {
            if ((prevSel && prevSel.id) != null) {
                const resolved = TreeUtils.findNode(prevSel.id, langData);
                if (resolved && (resolved.type === 'leaf' || resolved.type === 'exam')) {
                    nextSelected = resolved;
                }
            }
            if (!nextSelected && (prevPreview && prevPreview.id) != null) {
                const resolved = TreeUtils.findNode(prevPreview.id, langData);
                if (resolved && (resolved.type === 'leaf' || resolved.type === 'exam')) {
                    nextPreview = resolved;
                }
            }
        }

        store.update({ 
            activeSource: finalSource,
            data: langData, 
            rawGraphData: json,
            loading: false, 
            path: [langData], 
            lastActionMessage: null,
            // Same curriculum: re-resolve node ids so lesson/preview stay valid after load (local CRUD, etc.).
            // Different source: drop refs so stale pointers cannot survive a version switch.
            selectedNode: nextSelected,
            previewNode: nextPreview
        });

        if (store.state.constructionMode && !fileSystem.features.canWrite) {
            store.update({ constructionMode: false });
        }

        try {
            try {
                localStorage.setItem('arborito-active-source-id', finalSource.id);
                localStorage.setItem('arborito-active-source-meta', JSON.stringify(finalSource));
            } catch {
                /* quota / private mode, do not block graph load */
            }
            restoreTreeUiStateAfterLoad(store);
        } finally {
            store.dispatchEvent(new CustomEvent('graph-update'));
            queueMicrotask(() => {
                try {
                    scheduleSearchIndexRebuild(store);
                } catch (e) {
                    console.warn('search-index: schedule failed', e);
                }
            });
        }

        if (!suppressReadmeAutoOpen && !store.state.constructionMode) {
            setTimeout(() => {
                void runThrottledBackgroundTask(
                    `care-reminder:${store.state.activeSource?.id || 'tree'}`,
                    () => maybeNotifyCareDue(store),
                    { oncePerSession: true, minIntervalMs: 12000 }
                );
            }, 3000);
        }

        // Modals: only on full loadData (not internal refresh). Priority: versions after snapshot change > readme intro.
        if (!suppressReadmeAutoOpen && store.state.activeSource && !store.state.modal && !store.state.treeHydrating) {
            const pendingSwitch = consumePendingCurriculumSwitcher();
            if (pendingSwitch) {
                // Do not open version timeline after HTTPS snapshot change when destination is local garden / network (author);
                // constructionMode used to be required, but during plant/load it is still false → ghost modal.
                const skipReleasesAuto =
                    fileSystem.isLocal ||
                    fileSystem.isNostrTreeSource() ||
                    ((finalSource && finalSource.url) && String(finalSource.url).startsWith('branch://'));
                if (!skipReleasesAuto) {
                    setTimeout(() => {
                        if (store.state.modal || store.state.treeHydrating || store.state.constructionMode) return;
                        store.dispatchEvent(new CustomEvent('open-curriculum-switcher', { detail: { preferTab: 'version' } }));
                    }, 450);
                }
            } else {
                setTimeout(() => {
                    maybeNotifyRemoteVersionUpdate(store);
                }, 900);
            }
            /* Tree intro modal no longer auto-opens after load. The same content (description +
             * intro markdown) is the tree's own card in `tree-info`, reachable on-demand from the
             * map chrome ℹ️ and from each row in the tree picker. Auto-opening it on every load
             * felt intrusive and duplicated the description already visible elsewhere. */
        }
    },

    /**
     * SMART HYDRATION (THE COMPRESSION HACK)
     * If a branch ID is in `completedNodes`, automatically add all its children to the Set in memory.
     */
    hydrateCompletionState(store, rootNode) {
        const completedSet = store.userStore?.state?.completedNodes;
        if (!(completedSet instanceof Set)) return;
        let hydratedCount = 0;

        const traverse = (node) => {
            if (!node || typeof node !== 'object') return;
            // If parent is complete, ensure children are complete in memory
            if (completedSet.has(node.id)) {
                if (Array.isArray(node.children) && node.children.length > 0) {
                    node.children.forEach((child) => {
                        if (!completedSet.has(child.id)) {
                            completedSet.add(child.id);
                            hydratedCount++;
                        }
                        traverse(child);
                    });
                }
                /* Also expand leafIds when children are only partially loaded. */
                if (node.leafIds && Array.isArray(node.leafIds)) {
                    node.leafIds.forEach((id) => {
                        if (!completedSet.has(id)) {
                            completedSet.add(id);
                            hydratedCount++;
                        }
                    });
                }
            } else {
                if (Array.isArray(node.children)) node.children.forEach(traverse);
            }
        };
        
        if (rootNode) traverse(rootNode);
        if (hydratedCount > 0) {
            console.log(`Arborito hydration: expanded ${hydratedCount} implicit completions from compressed save.`);
        }
    }
};
